import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { usePermission } from '../../hooks/usePermission';
import { date } from '../../utils/format';
import { Empty, ErrorNotice, Loading, PageHeader, Table } from '../../components/ui';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { useToast } from '../../components/ui/Toast';

interface Year {
  id: string;
  code: string;
  start_date: string;
  end_date: string;
  periods: number;
  open_periods: number;
  series: number;
  is_current: boolean;
}

interface Period {
  id: string;
  period_no: number;
  start_date: string;
  end_date: string;
  status: string;
  closed_at: string | null;
  reopen_reason: string | null;
}

const TONE: Record<string, string> = {
  OPEN: 'text-success',
  SOFT_CLOSED: 'text-gold-700',
  CLOSED: 'text-ink-soft',
};

/**
 * Locking the books.
 *
 * Soft close still lets the accountant finish the month while the shop keeps
 * trading; a full close accepts nothing at all. Reopening is owner-only, needs
 * a reason, and the reason is kept permanently — a period that was reopened is
 * a thing an auditor is entitled to see.
 */
export function PeriodsPage() {
  const toast = useToast();
  const [opening, setOpening] = useState(false);
  const yearClient = useQueryClient();

  const years = useQuery({
    queryKey: ['financial-years'],
    queryFn: () => api.get<{ data: Year[] }>('/financial-years'),
  });
  const repair = useMutation({
    mutationFn: (id: string) =>
      api.post<{ code: string; periodsAdded: number; seriesAdded: number }>(`/financial-years/${id}/repair`, {}),
    onSuccess: (r) => {
      toast(
        r.periodsAdded + r.seriesAdded === 0 ? `${r.code} was already complete` : `${r.code} put right`,
        `${r.periodsAdded} months and ${r.seriesAdded} numbering series added`,
      );
      void yearClient.invalidateQueries({ queryKey: ['financial-years'] });
      void yearClient.invalidateQueries({ queryKey: ['periods'] });
    },
  });

  const openYear = useMutation({
    mutationFn: () => api.post<{ code: string; periods: number; series: number }>('/financial-years', {}),
    onSuccess: (r) => {
      toast(`${r.code} is open`, `${r.periods} months and ${r.series} numbering series ready`);
      void yearClient.invalidateQueries({ queryKey: ['financial-years'] });
      void yearClient.invalidateQueries({ queryKey: ['periods'] });
      setOpening(false);
    },
    onError: () => setOpening(false),
  });
  const { can } = usePermission();
  const client = useQueryClient();
  const [error, setError] = useState<unknown>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['periods'],
    queryFn: () => api.get<{ data: Period[] }>('/periods'),
  });

  const change = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'SOFT_CLOSED' | 'CLOSED' }) =>
      api.post(`/periods/${id}/close`, { status }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['periods'] }),
    onError: setError,
  });

  const reopen = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.post(`/periods/${id}/reopen`, { reason }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['periods'] }),
    onError: setError,
  });

  const rows = data?.data ?? [];

  return (
    <>
      {/* Without a year, nothing can be posted at all — so it is stated here
          rather than discovered at the till on the first of April. */}
      <section className="mb-5 rounded-xl border border-silver-100 bg-surface p-4 shadow-card">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold">Financial years</h2>
          {can('accounts.close_period') ? (
            <button
              className="btn-primary py-1.5 text-2xs"
              onClick={() => setOpening(true)}
              disabled={openYear.isPending}
            >
              {openYear.isPending ? 'Opening…' : 'Open the next year'}
            </button>
          ) : null}
        </div>

        {(years.data?.data ?? []).length === 0 ? (
          <p className="rounded-md border border-danger/30 bg-danger/10 px-3 py-2 text-sm">
            This shop has no financial year, so nothing can be posted. Open one to begin.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {(years.data?.data ?? []).map((y) => (
              <div
                key={y.id}
                className={`rounded-lg border px-3 py-2 text-sm ${
                  y.is_current ? 'border-gold-500 bg-gold-50' : 'border-silver-100'
                }`}
              >
                <p className="num font-semibold">
                  {y.code}
                  {y.is_current ? <span className="ml-2 text-2xs font-normal text-gold-700">this year</span> : null}
                </p>
                <p className="num text-2xs text-ink-soft">
                  {date(y.start_date)} – {date(y.end_date)}
                </p>
                <p className="text-2xs text-ink-faint">
                  {y.open_periods} of {y.periods} months open · {y.series} numbering series
                </p>
                {/* A year with no months is listed like any other and refuses
                    every document. Say so, and offer the repair. */}
                {y.periods < 12 || y.series === 0 ? (
                  <div className="mt-1.5 rounded border border-danger/30 bg-danger/10 px-2 py-1.5">
                    <p className="text-2xs text-danger">
                      {y.periods === 0
                        ? 'This year has no months, so nothing can be posted into it.'
                        : y.periods < 12
                          ? `Only ${y.periods} of its 12 months exist.`
                          : 'This year has no numbering, so documents cannot be numbered.'}
                    </p>
                    {can('accounts.close_period') ? (
                      <button
                        className="mt-1 text-2xs font-medium text-gold-700 underline"
                        disabled={repair.isPending}
                        onClick={() => repair.mutate(y.id)}
                      >
                        {repair.isPending ? 'Putting it right…' : 'Add what is missing'}
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </section>

      <PageHeader
        title="Financial periods"
        subtitle="Once a month is closed, nothing can be posted into it without the owner reopening it — and that is recorded."
      />
      <ErrorNotice error={error} />

      {isLoading ? <Loading /> : null}
      {!isLoading && rows.length === 0 ? <Empty message="No periods set up." /> : null}

      {rows.length > 0 ? (
        <Table head={['Period', 'From', 'To', 'Status', 'Closed', '']}>
          {rows.map((p) => (
            <tr key={p.id}>
              <td className="td num">{p.period_no}</td>
              <td className="td">{date(p.start_date)}</td>
              <td className="td">{date(p.end_date)}</td>
              <td className={`td ${TONE[p.status] ?? ''}`}>
                {p.status.replace(/_/g, ' ').toLowerCase()}
                {p.reopen_reason ? (
                  <span className="block text-xs text-ink-soft">reopened: {p.reopen_reason}</span>
                ) : null}
              </td>
              <td className="td text-xs">{p.closed_at ? date(p.closed_at) : '—'}</td>
              <td className="td">
                <div className="flex gap-2">
                  {p.status === 'OPEN' && can('accounts.close_period') ? (
                    <button
                      className="btn-ghost py-1 text-xs"
                      onClick={() => change.mutate({ id: p.id, status: 'SOFT_CLOSED' })}
                    >
                      Soft close
                    </button>
                  ) : null}
                  {p.status === 'SOFT_CLOSED' && can('accounts.close_period') ? (
                    <button
                      className="btn-ghost py-1 text-xs"
                      onClick={() => change.mutate({ id: p.id, status: 'CLOSED' })}
                    >
                      Close
                    </button>
                  ) : null}
                  {p.status !== 'OPEN' && can('accounts.reopen_period') ? (
                    <button
                      className="py-1 text-xs text-danger underline"
                      onClick={() => {
                        const reason = window.prompt(
                          'Why is this period being reopened? The reason is kept permanently.',
                        );
                        if (reason && reason.trim().length >= 3) reopen.mutate({ id: p.id, reason });
                      }}
                    >
                      Reopen
                    </button>
                  ) : null}
                </div>
              </td>
            </tr>
          ))}
        </Table>
      ) : null}

      <p className="mt-4 max-w-2xl text-xs text-ink-soft">
        Soft close keeps the month open to anyone holding the permission to post into a soft-closed period — for the
        accountant finishing off while the counter keeps trading. A full close accepts nothing.
      </p>

      <ConfirmDialog
        open={opening}
        title="Open the next financial year?"
        message="The year is created with its twelve months and a numbering series for every document, copied from this year's prefixes. Nothing already posted is touched."
        detail={<p>Until a year exists for a date, nothing can be posted on it — no bill, no purchase, no receipt.</p>}
        confirmLabel="Open it"
        busy={openYear.isPending}
        onCancel={() => setOpening(false)}
        onConfirm={() => openYear.mutate()}
      />
    </>
  );
}
